import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'
import { connect } from 'react-redux'

import { fetchCityStateFromZip, fetchZipcodeFromIP, stepSelected, updateLead } from 'common/actions'
import {
  applicationFieldBlurred,
  applicationFieldChanged,
  applicationFieldRegistered,
  applicationFieldUnregistered,
  applicationUpdateValidationStatus,
} from 'common/actions/application'
import { creditScoreOfferUrlSelector } from 'embed/selectors'
import { Field, FieldDataValue, PublicClassNames, ReturnToSearchText, Waypoints } from 'embed/constants'
import { Fields } from 'embed/constants/fields'
import { CreditOffer } from 'embed/constants/models'

import AdvertiserDisclosure from 'embed/components/AdvertiserDisclosure'
import { ApplicationField } from 'embed/components/ApplicationField/container'
import ApplicationSubmitButton from 'embed/components/ApplicationSubmitButton/container'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import { Brand, ConfigKeys, CreditCardStep, Filter, Product } from 'even-ts-static'
import { SelectedCard } from '../SelectedCard'

import { NoAffect } from 'embed/components/Application/NoAffect'
import { ReturnToSearch } from 'embed/components/Application/ReturnToSearch'
import { TCPALanguage } from 'embed/components/Application/TCPALanguage'
import { brandSelector, configShouldDisplayBackToSearchSelector, configStepSelector } from 'embed/selectors/config'
import { MultiProductState } from 'embed/reducers'
import Waypoint from '../../../Waypoint'

const styles = require('embed/styles/application')
const CheckBlue = require('./check-blue.svg')

interface OwnProps {
  collectFinancialInformation: boolean
  handleConsentsToFCRAandTCPA: () => void
}

interface StoreProps {
  brand: Brand
  configuredForIntroStep: boolean
  consent: boolean
  consentValidationStatus: boolean
  creditScoreOfferUrl: string | undefined
  directReferral: boolean
  hasCalledGeocoder: boolean
  isCreditRatingFieldValid: boolean
  selectedOffer?: CreditOffer
  shouldDisplayBackToSearch: boolean
  zipcode?: FieldDataValue
}

interface DispatchProps {
  dispatchApplicationFieldBlurred: (field: Field) => void
  dispatchApplicationFieldChanged: (field: Field, value: number | string) => void
  dispatchApplicationFieldRegistered: (field: Field) => void
  dispatchApplicationFieldUnregistered: (field: Field) => void
  dispatchApplicationUpdateValidationStatus: () => void
  dispatchBackToIntro: () => void
  dispatchBackToStepOne: () => void
  dispatchFetchCityStateFromZip: (zipcode: FieldDataValue) => void
  dispatchFetchZipcodeFromIP: () => void
  dispatchUpdateLead: () => void
}

const BUTTON_TEXT = 'Agree and Continue'

export type CreditCardCommonFormProps = DispatchProps & StoreProps & OwnProps

const CreditCardReturnToSearch = ({ configuredForIntroStep, dispatchBackToIntro, dispatchBackToStepOne }) => {
  const handleReturnToOriginalStep = configuredForIntroStep ? dispatchBackToIntro : dispatchBackToStepOne
  return configuredForIntroStep ? (
    <ReturnToSearch
      text={ReturnToSearchText.Back}
      className={styles.backToSearch}
      onClick={handleReturnToOriginalStep}
    />
  ) : (
    <ReturnToSearch className={styles.backToSearch} onClick={handleReturnToOriginalStep} />
  )
}

export const CreditCardCommonFormView: React.FC<CreditCardCommonFormProps> = ({
  collectFinancialInformation,
  configuredForIntroStep,
  directReferral,
  dispatchFetchZipcodeFromIP,
  dispatchFetchCityStateFromZip,
  dispatchBackToIntro,
  dispatchBackToStepOne,
  dispatchUpdateLead,
  handleConsentsToFCRAandTCPA,
  hasCalledGeocoder,
  selectedOffer,
  shouldDisplayBackToSearch,
  zipcode,
}) => {
  React.useEffect(() => {
    if (hasCalledGeocoder === false) {
      dispatchFetchZipcodeFromIP()
    } else if (zipcode !== undefined) {
      dispatchFetchCityStateFromZip(zipcode)
    }
  }, [])

  React.useEffect(() => {
    dispatchUpdateLead()
  }, [])

  return (
    <ThemeConsumer>
      {({
        backdropStyles,
        borderRadiusLarge,
        boxStyles,
        borderRadiusSmall,
        colorBorder,
        colorPrimary,
        fontFamilyPrimary,
      }) => (
        <React.Fragment>
          <div
            className={classnames(
              PublicClassNames.ApplicationContainer,
              styles.container,
              css`
                background-color: ${backdropStyles.backgroundColor};
                border-color: ${colorBorder};
                border-radius: ${borderRadiusLarge};
              `,
            )}
            style={boxStyles}
          >
            {shouldDisplayBackToSearch && !directReferral && (
              <CreditCardReturnToSearch
                configuredForIntroStep={configuredForIntroStep}
                dispatchBackToIntro={dispatchBackToIntro}
                dispatchBackToStepOne={dispatchBackToStepOne}
              />
            )}
            <NoAffect />
            {selectedOffer && (
              <SelectedCard>
                <div
                  className={classnames(
                    styles.marketingBlurb,
                    css`
                      border-color: ${colorBorder};
                      border-radius: ${borderRadiusSmall};
                    `,
                  )}
                >
                  <div className={styles.marketingBlurbTitle}>You are seconds away from matched offers</div>
                  <ul
                    className={classnames(
                      styles.marketingBlurbList,
                      css`
                        svg circle {
                          fill: ${colorPrimary};
                        }
                      `,
                    )}
                  >
                    <li className={styles.marketingBlurbListItem}>
                      <div>
                        <CheckBlue />
                      </div>
                      <div>See if you qualify for the offer you selected</div>
                    </li>
                    <li className={styles.marketingBlurbListItem}>
                      <div>
                        <CheckBlue />
                      </div>
                      <div>Be instantly matched with other offers from our top providers</div>
                    </li>
                    <li className={styles.marketingBlurbListItem}>
                      <div>
                        <CheckBlue />
                      </div>
                      <div>No impact on your credit score</div>
                    </li>
                  </ul>
                </div>
              </SelectedCard>
            )}
            <section className={styles.formSection}>
              <h2
                className={classnames(
                  styles.heading,
                  css`
                    font-family: ${fontFamilyPrimary};
                  `,
                )}
              >
                Your Information
              </h2>
              {!selectedOffer && (
                <div className={styles.advertiserDisclosure}>
                  <AdvertiserDisclosure />
                </div>
              )}
              <ApplicationField product={Product.CreditCard} field={Fields.FirstName} />
              <ApplicationField product={Product.CreditCard} field={Fields.LastName} />
              <ApplicationField product={Product.CreditCard} field={Fields.EmailAddress} />
              <ApplicationField product={Product.CreditCard} field={Fields.DateOfBirth} />
              <ApplicationField product={Product.CreditCard} field={Fields.AddressOne} />
              <ApplicationField product={Product.CreditCard} field={Fields.AddressTwo} />
              <ApplicationField product={Product.CreditCard} field={Fields.Zipcode} />
              <ApplicationField
                product={Product.CreditCard}
                field={Fields.City}
                styles={css`
                  width: calc(30% - (1rem * 2));
                `}
              />
              <ApplicationField
                product={Product.CreditCard}
                field={Fields.State}
                styles={css`
                  width: calc(20% - (1rem * 2));
                `}
              />
              <Waypoint name={Waypoints.YourInfo} />
            </section>
            {collectFinancialInformation && (
              <section className={styles.formSection}>
                <h2
                  className={classnames(
                    styles.heading,
                    css`
                      font-family: ${fontFamilyPrimary};
                    `,
                  )}
                >
                  Financial Information
                </h2>
                <ApplicationField product={Product.CreditCard} field={Fields.EmploymentStatus} />
                <ApplicationField product={Product.CreditCard} field={Fields.AnnualIncomeCredit} />
                <ApplicationField product={Product.CreditCard} field={Fields.PayFrequency} />
                <Waypoint name={Waypoints.FinancialInfo} />
              </section>
            )}

            <Waypoint name={Waypoints.SubmitBtn} />
            <ApplicationSubmitButton
              product={Product.CreditCard}
              nextStep={CreditCardStep.Results}
              handleBeforeSubmit={handleConsentsToFCRAandTCPA}
            >
              <span className={styles.consentButtonLabel}>{BUTTON_TEXT}</span>
            </ApplicationSubmitButton>
            <TCPALanguage buttonText={BUTTON_TEXT} className={styles.consentWithButton} product={Product.CreditCard} />
          </div>
        </React.Fragment>
      )}
    </ThemeConsumer>
  )
}

const mapStateToProps = (state: MultiProductState): StoreProps => {
  const configStep = configStepSelector(state)
  const configuredForIntroStep = configStep === CreditCardStep.Intro
  return {
    brand: brandSelector(state),
    configuredForIntroStep,
    consent:
      !!state.application[Product.CreditCard]?.fields?.[Filter.ConsentsToFCRA]?.value &&
      !!state.application[Product.CreditCard]?.fields?.[Filter.ConsentsToTCPA]?.value,
    consentValidationStatus:
      !!state.application[Product.CreditCard]?.fields?.[Filter.ConsentsToFCRA]?.shouldShowValidationStatus ||
      !!state.application[Product.CreditCard]?.fields?.[Filter.ConsentsToTCPA]?.shouldShowValidationStatus,
    creditScoreOfferUrl: creditScoreOfferUrlSelector(state),
    directReferral: state.config[ConfigKeys.Step] === CreditCardStep.PreQualifiedForm,
    hasCalledGeocoder: state.ui.hasCalledGeocoder,
    isCreditRatingFieldValid: !!state.application[Product.CreditCard]?.fields?.[Filter.CreditScore]?.isValid,
    selectedOffer: state.rates.selected[Product.CreditCard],
    shouldDisplayBackToSearch: configShouldDisplayBackToSearchSelector(state),
    zipcode: state.application[Product.CreditCard]?.fields?.[Filter.Zipcode]?.value,
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchApplicationFieldBlurred: (field: Field) => dispatch(applicationFieldBlurred(Product.CreditCard, field.key)),
  dispatchApplicationFieldChanged: (field: Field, value: number | string) =>
    dispatch(applicationFieldChanged(Product.CreditCard, field.key, value)),
  dispatchApplicationFieldRegistered: (field: Field) =>
    dispatch(applicationFieldRegistered(Product.CreditCard, field.key)),
  dispatchApplicationFieldUnregistered: (field: Field) =>
    dispatch(applicationFieldUnregistered(Product.CreditCard, field.key)),
  dispatchApplicationUpdateValidationStatus: () => dispatch(applicationUpdateValidationStatus(Product.CreditCard)),
  dispatchBackToIntro: () => dispatch(stepSelected(Product.CreditCard, CreditCardStep.Intro)),
  dispatchBackToStepOne: () => dispatch(stepSelected(Product.CreditCard, CreditCardStep.Preview)),
  dispatchFetchCityStateFromZip: (zipcode) => dispatch(fetchCityStateFromZip(Product.CreditCard, zipcode)),
  dispatchFetchZipcodeFromIP: () => dispatch(fetchZipcodeFromIP(Product.CreditCard, false)),
  dispatchUpdateLead: () => dispatch(updateLead(Product.CreditCard)),
})

export const CreditCardCommonForm = connect<StoreProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps,
)(CreditCardCommonFormView)
